import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from "recharts";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/PageHeader";
import { HealthBadge } from "@/components/HealthBadge";
import { PercentBar } from "@/components/PercentBar";
import { EmptyState } from "@/components/EmptyState";
import { CreateProjectDialog } from "@/pages/CreateProjectDialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "@/components/ui/table";
import { pct, fmtNum } from "@/lib/format";
import { getErrorMessage } from "@/lib/errors";
import { toast } from "sonner";
import { Zap, AlertTriangle, ListChecks, Gauge } from "lucide-react";

const HEALTH_COLOR = { ON_TRACK: "#10B981", AT_RISK: "#F5B800", CRITICAL: "#EF4444" };
const SORT_OPTIONS = [
  { value: "risk", label: "Delay risk" },
  { value: "progress", label: "Overall progress" },
  { value: "variance", label: "Schedule variance" },
  { value: "issues", label: "Open issues" },
  { value: "name", label: "Name" },
  { value: "capacity", label: "Capacity" },
];

export default function Portfolio() {
  const { isOrgAdmin } = useAuth();
  const navigate = useNavigate();
  const [sortBy, setSortBy] = useState("risk");
  const [overview, setOverview] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async (sort = sortBy) => {
    setLoading(true);
    try {
      const { data } = await api.get("/portfolio", { params: { sort_by: sort } });
      setOverview(data);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }, [sortBy]);

  useEffect(() => { load(sortBy); }, [sortBy, load]);

  const chartData = useMemo(
    () => (overview?.projects || []).map((p) => ({ code: p.code, risk_score: p.risk_score, health: p.health })),
    [overview]
  );

  const totals = overview?.totals;

  return (
    <div className="space-y-6" data-testid="portfolio-page">
      <PageHeader
        title="Portfolio Command Center"
        description={overview ? `${totals.projects} projects · as of ${overview.as_of}` : "Loading your organisation's projects…"}
        action={isOrgAdmin && <CreateProjectDialog onCreated={() => load()} />}
      />

      <div className="grid grid-cols-1 gap-3.5 sm:grid-cols-2 lg:grid-cols-4" data-testid="portfolio-totals-cards">
        <StatCard icon={<Zap className="h-4 w-4" />} label="Capacity under tracking" value={`${fmtNum(totals?.capacity_mwp, 1)} MWp`} testId="stat-capacity" />
        <StatCard icon={<Gauge className="h-4 w-4" />} label="Weighted progress" value={pct(totals?.capacity_weighted_progress_pct)} testId="stat-progress" />
        <StatCard
          icon={<AlertTriangle className="h-4 w-4" />}
          label="Health split"
          value={`${totals?.on_track ?? 0} / ${totals?.at_risk ?? 0} / ${totals?.critical ?? 0}`}
          hint="On track / At risk / Critical"
          testId="stat-health"
        />
        <StatCard icon={<ListChecks className="h-4 w-4" />} label="Open issues · alerts" value={`${totals?.open_issues ?? 0} · ${totals?.open_alerts ?? 0}`} testId="stat-issues" />
      </div>

      {chartData.length > 0 && (
        <div className="rounded-xl border border-border bg-card p-4" data-testid="portfolio-risk-chart-container">
          <h2 className="mb-3 text-sm font-semibold uppercase tracking-wider text-muted-foreground">Delay Risk by Project</h2>
          <ResponsiveContainer width="100%" height={180}>
            <BarChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#1E293B" vertical={false} />
              <XAxis dataKey="code" stroke="#64748b" fontSize={11} />
              <YAxis stroke="#64748b" fontSize={11} domain={[0, 100]} />
              <Tooltip contentStyle={{ background: "#0F172A", border: "1px solid #334155", borderRadius: 8, fontSize: 12 }} />
              <Bar dataKey="risk_score" radius={[4, 4, 0, 0]}>
                {chartData.map((entry, i) => (
                  <Cell key={i} fill={HEALTH_COLOR[entry.health] || "#F5B800"} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}

      <div className="rounded-xl border border-border bg-card" data-testid="portfolio-projects-table-container">
        <div className="flex items-center justify-between border-b border-border p-4">
          <h2 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground">Projects ranked by</h2>
          <Select value={sortBy} onValueChange={setSortBy}>
            <SelectTrigger className="w-48" data-testid="portfolio-sort-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              {SORT_OPTIONS.map((o) => (
                <SelectItem key={o.value} value={o.value} data-testid={`sort-option-${o.value}`}>{o.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {!loading && (overview?.projects?.length ?? 0) === 0 ? (
          <EmptyState title="No projects yet" description="Create your first EPC project to start tracking progress." testId="portfolio-empty-state" />
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Project</TableHead>
                <TableHead>Health</TableHead>
                <TableHead>Progress</TableHead>
                <TableHead>Variance</TableHead>
                <TableHead>Capacity</TableHead>
                <TableHead>Days left</TableHead>
                <TableHead>Issues</TableHead>
                <TableHead>Alerts</TableHead>
                <TableHead>Risk</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {(overview?.projects || []).map((p) => (
                <TableRow
                  key={p.project_id}
                  data-testid={`portfolio-project-row-${p.project_id}`}
                  className="cursor-pointer"
                  onClick={() => navigate(`/projects/${p.project_id}`)}
                >
                  <TableCell>
                    <p className="font-medium text-foreground">{p.name}</p>
                    <p className="font-mono text-xs text-muted-foreground">{p.code} · {p.location || "—"}</p>
                  </TableCell>
                  <TableCell><HealthBadge health={p.health} testId={`health-badge-${p.project_id}`} /></TableCell>
                  <TableCell className="min-w-[140px]">
                    <div className="flex items-center gap-2">
                      <PercentBar actual={p.overall_actual_pct} planned={p.overall_planned_pct} testId={`percent-bar-${p.project_id}`} />
                      <span className="font-mono text-xs text-foreground">{pct(p.overall_actual_pct)}</span>
                    </div>
                  </TableCell>
                  <TableCell className={`font-mono text-xs ${p.variance_pct < 0 ? "text-rose-400" : "text-emerald-400"}`}>
                    {p.variance_pct >= 0 ? "+" : ""}{pct(p.variance_pct)}
                  </TableCell>
                  <TableCell className="font-mono text-xs text-foreground">{fmtNum(p.dc_capacity_mwp, 1)} MWp</TableCell>
                  <TableCell className="font-mono text-xs text-foreground">{p.days_left ?? "—"}</TableCell>
                  <TableCell className="font-mono text-xs text-foreground">{p.open_issues}</TableCell>
                  <TableCell className="font-mono text-xs text-foreground">{p.open_alerts} {p.critical_alerts > 0 && <span className="text-rose-400">({p.critical_alerts} crit)</span>}</TableCell>
                  <TableCell className="font-mono text-sm font-bold text-foreground">{fmtNum(p.risk_score, 1)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>
    </div>
  );
}

function StatCard({ icon, label, value, hint, testId }) {
  return (
    <div className="rounded-xl border border-border bg-card p-4" data-testid={testId}>
      <div className="flex items-center gap-2 text-muted-foreground">
        {icon}
        <p className="text-xs font-semibold uppercase tracking-wider">{label}</p>
      </div>
      <p className="mt-2 font-mono text-2xl font-bold text-foreground">{value}</p>
      {hint && <p className="mt-0.5 text-[11px] text-muted-foreground">{hint}</p>}
    </div>
  );
}
